/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, Stack, Alert, CircularProgress,
    TextField, Autocomplete, Checkbox, FormControlLabel,
} from '@mui/material';
import Iconify from '../Iconify';
import { formatSlotLabel, formatSlotTime, calcEndTime } from '../../utils/pmsTimeUtils';
import { callApi } from '../../utils/apiCall';
import { staffBookingPatient, staffPatientIsMinor } from '../../utils/pmsStaffPatient';
import { useSelector } from '../../redux/store';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';

const BOOKING_STEPS = [
    { index: 1, label: 'Confirming appointment' },
    { index: 2, label: 'Updating patient record' },
    { index: 3, label: 'Finalising booking' },
];


/**
 * PMS Booking Confirmation Wizard Step — embedded within the standard CRM follow-up flow.
 *
 * Active when pmsEnabledGlobal=true AND dentallyEnabledGlobal=false.
 *
 * Submits to StaffBookingSerializer (commit 4fb2d3c5) via
 * POST /api/v1/practices/<practice_pk>/pms/book/.
 *
 * Payload shape (the serializer uses a nested ``slot`` field — not flat):
 *   {
 *     slot: { start_time, end_time, practitioner_id, schedule_id },
 *     notes, assignees: [int],
 *     treatment_id?, lead_id?
 *   }
 *
 * The endpoint handles three caller-differentiated paths by which identity
 * fields are supplied:
 *   1. OverView    — lead_id + treatment_id both present
 *   2. NewLead     — lead_id only (BE picks the lead's first LeadTreatment,
 *                    or returns 400 if the lead has multiple treatments)
 *   3. CreateLead — treatment_id plus validated patient/contact/guardian fields,
 *                    or an explicitly selected existing lead_id.
 *
 * Props:
 *   practiceId          — required
 *   pmsSlot             — selected slot object from PmsAvailabilityWizardStep,
 *                         carrying ISO datetimes in start_time/end_time
 *   existingLead        — lead object (id, first_name, last_name, lead_contact)
 *   existingTreatmentId — integer treatment record ID (optional)
 *   onClose             — (success: boolean) => void
 *   onStepStateChange   — ({ buttonLabel, buttonIcon, buttonDisabled, buttonLoading }) => void
 *   appointmentDuration — slot length in minutes, displayed only (BE derives
 *                         real duration from slot.start_time/end_time)
 */
const PmsBookingConfirmStep = forwardRef(({
    pmsSlot,
    practiceUsers,
    timeZone,
    practiceId,
    existingLead,
    existingTreatmentId,
    newPatient,
    submissionStatus,
    beginSubmission,
    onSubmissionStatusChange,
    onClose,
    onStepStateChange,
    appointmentDuration = 30,
}, ref) => {
    const allUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);

    // NOTE: Cannot use FilterAssigneeList utility here because it returns `id: el?.user?.id`
    // (the Django User id), whereas the PMS booking API expects the practiceUser membership id (`u.id`).
    // The payload uses `assignees: assignees.map((u) => u.id)` — keep inline filter to preserve correct id.
    const assignableUsers = (practiceUsers || allUsers)
        .filter((u) => u?.is_enabled && u?.role !== 'ADMIN' && u?.role !== 'SUPPORT' && !u?.user?.is_superuser)
        .map((u) => ({
            id: u.id,
            name: `${u?.user?.first_name || ''} ${u?.user?.last_name || ''}`.trim(),
        }));

    const [notes, setNotes] = useState('');
    const [assignees, setAssignees] = useState([]);

    const [loading, setLoading] = useState(false);
    const [bookingStep, setBookingStep] = useState(0);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [unknownOutcome, setUnknownOutcome] = useState(false);
    const [guardianDetails, setGuardianDetails] = useState({});

    useEffect(() => { setGuardianDetails({}); }, [newPatient]);

    const practitionerName = pmsSlot?._practitioner_name || 'Any available practitioner';
    const patientData = useMemo(() => staffBookingPatient({ ...newPatient, ...guardianDetails }), [newPatient, guardianDetails]);
    const isMinor = staffPatientIsMinor(patientData.date_of_birth);
    const needsGuardian = isMinor || patientData.booked_on_behalf;
    const hasContact = patientData.email || patientData.phone || (isMinor && (patientData.guardian_email || patientData.guardian_phone));
    const guardianReady = !needsGuardian || (patientData.guardian_name && (!isMinor || patientData.guardian_email || patientData.guardian_phone));
    const patientReady = !!existingLead?.id || !!(patientData.first_name && patientData.last_name && hasContact && guardianReady);
    const slotMinutes = pmsSlot?.end_time ? Math.round((new Date(pmsSlot.end_time) - new Date(pmsSlot.start_time)) / 60000) : appointmentDuration;
    const canConfirm = pmsSlot && existingTreatmentId && patientReady && !loading && !success && !unknownOutcome && (!submissionStatus || submissionStatus === 'idle');

    // Report step state to parent so the footer can show the right button
    useEffect(() => {
        onStepStateChange?.({
            buttonLabel: 'Confirm Booking',
            buttonIcon: 'mdi:check-bold',
            buttonDisabled: !canConfirm,
            buttonLoading: loading,
            hideFooterOnSuccess: success,
        });
    }, [canConfirm, loading, success]);

    const handleConfirm = useCallback(async () => {
        if (!canConfirm) return;
        if (loading || (beginSubmission && !beginSubmission())) return;
        setError(null);
        setLoading(true);
        setBookingStep(1);

        try {
            // Preserve the server's aware slot identity; native treatment duration
            // and patient visit classification are revalidated by the backend.
            const payload = {
                slot: {
                    start_time: pmsSlot.start_time,
                    end_time: pmsSlot.end_time || pmsSlot.finish_time,
                    practitioner_id: pmsSlot.practitioner_id,
                    schedule_id: pmsSlot._schedule_id,
                },
                notes,
                assignees: assignees.map((u) => u.id),
                ...(existingTreatmentId ? { treatment_id: existingTreatmentId } : {}),
                ...(existingLead?.id ? { lead_id: existingLead.id } : { ...patientData, booked_on_behalf: needsGuardian }),
            };

            setBookingStep(2);
            const r = await callApi(`/api/v1/practices/${practiceId}/pms/book/`, 'POST', payload);

            setBookingStep(3);
            if (r.ok) {
                onSubmissionStatusChange?.('success');
                setSuccess(true);
                onClose?.(true, r.data);
                return;
            }

            // Documented error codes on this endpoint (StaffBookingSerializer + view):
            //   400 validation / "slot just taken" / "lead has no treatments"
            //   403 user not a member of practice
            //   404 schedule/treatment not in practice, PMS disabled
            //   429 throttled (shared PatientFacingThrottle)
            //   503 global kill-switch (Feature.is_active=False)
            let msg;
            if (!r?.status || r.status >= 500) {
                onSubmissionStatusChange?.('unknown');
                setUnknownOutcome(true);
                msg = 'The booking outcome could not be verified. Check the diary and patient record before starting another booking; this request may have completed.';
            } else if (r?.status === 403) {
                msg = "You don't have permission to book on this schedule.";
            } else if (r?.status === 404) {
                msg = r?.data?.detail || 'That schedule or treatment is no longer available. Please re-select.';
            } else if (r?.status === 429) {
                msg = 'Too many booking attempts. Please wait a moment and try again.';

            } else {
                // 400 and anything else — prefer the server's detail, fall back to first field error.
                const firstFieldError = r?.data && typeof r.data === 'object'
                    ? Object.values(r.data).find((v) => Array.isArray(v) && v.length)?.[0]
                    : null;
                msg = r?.data?.detail || firstFieldError || r?.data?.error || r?.data?.message || 'Booking failed. Please try again.';
            }
            if (r?.status && r.status < 500) onSubmissionStatusChange?.('idle');
            setError(msg);
        } catch {
            onSubmissionStatusChange?.('unknown');
            setUnknownOutcome(true);
            setError('The booking outcome could not be verified. Check the diary and patient record before starting another booking; this request may have completed.');
        } finally {
            setLoading(false);
            setBookingStep(0);
        }
    }, [canConfirm, pmsSlot, notes, assignees, existingTreatmentId, existingLead, patientData, needsGuardian, practiceId, onClose, beginSubmission, onSubmissionStatusChange]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) return null;
            handleConfirm();
            return null;
        },
    }), [canConfirm, handleConfirm]);

    useEffect(() => { if (submissionStatus === 'idle') { setUnknownOutcome(false); setError(null); } }, [submissionStatus]);

    // Success state
    if (success) {
        return (
            <Box sx={{ py: 5, textAlign: 'center' }}>
                <Box sx={{
                    width: 72, height: 72, borderRadius: '50%',
                    bgcolor: 'rgba(0,171,85,0.10)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5,
                }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00AB55" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </Box>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#1d1d1f', mb: 0.5 }}>
                    Appointment booked
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: '#86868b' }}>
                    The appointment has been added to the PMS schedule.
                </Typography>
            </Box>
        );
    }

    // Loading / progress state
    if (loading || submissionStatus === 'pending') {
        return (
            <Box sx={{ py: 5, textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2.5, color: '#00AB55' }} />
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#1d1d1f', mb: 1.5 }}>
                    Booking in progress…
                </Typography>
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_STEPS.map(({ index, label }) => {
                        let icon;
                        if (bookingStep > index) icon = <Iconify icon="mdi:check-circle" width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
                        else if (bookingStep === index) icon = <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
                        else icon = <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
                        return (
                            <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                {icon}
                                <Typography variant="body2" sx={{
                                    color: bookingStep >= index ? 'text.primary' : 'text.disabled',
                                    fontWeight: bookingStep === index ? 600 : 400,
                                }}>
                                    {label}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    const patient = existingLead || { ...patientData, lead_contact: { email: patientData.email, phone: patientData.phone } };
    const patientName = [patient.first_name, patient.last_name].filter(Boolean).join(' ');

    return (
        <Box>
            {!patientReady && <Alert severity="warning" sx={{ mb: 2 }}>Return to Personal Details if the patient name or contact is missing. Complete any required guardian details below before confirming.</Alert>}
            {!existingTreatmentId && <Alert severity="warning" sx={{ mb: 2 }}>Choose an appointment treatment before confirming.</Alert>}
            {!existingLead?.id && <Stack spacing={1} sx={{ mb: 2 }}>
                <FormControlLabel label="Booking on behalf of the patient" control={<Checkbox checked={needsGuardian} disabled={isMinor || unknownOutcome} onChange={(event) => setGuardianDetails({ ...guardianDetails, booked_on_behalf: event.target.checked })} />} />
                {isMinor && <Typography variant="body2">Patients under 16 need a guardian name and guardian contact.</Typography>}
                {needsGuardian && <>
                    <TextField label="Guardian name" required value={patientData.guardian_name} inputProps={{ maxLength: 120 }} onChange={(event) => setGuardianDetails({ ...guardianDetails, guardian_name: event.target.value })} />
                    <TextField label="Relationship to patient" value={patientData.guardian_relation} inputProps={{ maxLength: 60 }} onChange={(event) => setGuardianDetails({ ...guardianDetails, guardian_relation: event.target.value })} />
                    <TextField label="Guardian email" type="email" value={patientData.guardian_email} inputProps={{ maxLength: 254 }} onChange={(event) => setGuardianDetails({ ...guardianDetails, guardian_email: event.target.value })} />
                    <TextField label="Guardian phone" value={patientData.guardian_phone} inputProps={{ maxLength: 20 }} onChange={(event) => setGuardianDetails({ ...guardianDetails, guardian_phone: event.target.value })} />
                </>}
            </Stack>}
            {/* Patient summary */}
            {patientReady && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, mb: 2, borderRadius: 2,
                    bgcolor: 'rgba(7,141,238,0.04)',
                    border: '1px solid rgba(7,141,238,0.16)',
                }}>
                    <Iconify icon="mdi:account-circle" width={22} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{patientName}</Typography>
                        {patient?.lead_contact?.email && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {patient.lead_contact.email}
                            </Typography>
                        )}
                        {existingLead?.lead_contact?.phone && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Iconify icon="mdi:phone-outline" width={12} sx={{ color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {existingLead.lead_contact.phone}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                </Box>
            )}

            {/* Slot summary */}
            {pmsSlot && (
                <Box sx={{
                    p: 1.5, bgcolor: 'rgba(0,171,85,0.08)',
                    borderRadius: 2.5, mb: 2,
                    border: '1px solid rgba(0,171,85,0.3)',
                }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="mdi:calendar-check" width={18} sx={{ color: 'success.main' }} />
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {formatSlotLabel(pmsSlot.start_time, pmsSlot._timezone || timeZone)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatSlotTime(pmsSlot.start_time, pmsSlot._timezone || timeZone)} &ndash; {pmsSlot.end_time ? formatSlotTime(pmsSlot.end_time, pmsSlot._timezone || timeZone) : calcEndTime(pmsSlot.start_time, slotMinutes, pmsSlot._timezone || timeZone)} &middot; {slotMinutes} min
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {practitionerName}{(pmsSlot._timezone || timeZone) ? ` · ${pmsSlot._timezone || timeZone}` : ''}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            )}

            {/* Appointment details */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:clipboard-text-outline" width={15} sx={{ color: 'primary.main' }} />
                    Appointment Details
                </Typography>

                {/* Assignees */}
                <Autocomplete
                    multiple
                    size="small"
                    sx={{ mb: 2 }}
                    options={assignableUsers}
                    getOptionLabel={(u) => u.name}
                    value={assignees}
                    onChange={(_, val) => setAssignees(val)}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Assignees"
                            placeholder="Search users…"
                        />
                    )}
                />

                {/* Notes */}
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>
    );
});

export default PmsBookingConfirmStep;
